import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, DollarSign, Award, Upload, X, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * CursosExtensionPreadmision - Cursos de extensión para estudiantes de preadmisión
 * Mismo diseño que TalleresEstudiante pero adaptado para preadmisión
 */
const CursosExtensionPreadmision = () => {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [comprobante, setComprobante] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
    const [paymentConfig, setPaymentConfig] = useState(null);

    useEffect(() => {
        loadCursos();
        loadPaymentConfig();
    }, [searchTerm]);

    const loadPaymentConfig = async () => {
        try {
            const response = await fetch(`${API_URL}/auth/payment-config/extensiones`);
            if (response.ok) {
                const data = await response.json();
                setPaymentConfig(data);
            }
        } catch (error) {
            console.error('Error loading payment config:', error);
        }
    };

    const loadCursos = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

            const response = await fetch(`${API_URL}/estudiante/cursos-extension?search=${searchTerm}`, { headers });
            if (response.ok) {
                const data = await response.json();
                setCursos(data.cursos || []);
            }
        } catch (error) {
            console.error('Error loading cursos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInscribirse = (curso) => {
        setSelectedCurso(curso);
        setShowModal(true);
        setComprobante(null);
        setMensaje({ tipo: '', texto: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!comprobante) {
            setMensaje({ tipo: 'error', texto: 'Debe subir el comprobante de pago' });
            return;
        }

        setEnviando(true);
        try {
            const token = localStorage.getItem('token');
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            const formData = new FormData();
            formData.append('id_curso', selectedCurso.id_curso);
            formData.append('comprobante_pago', comprobante);
            formData.append('id_usuario', user.id_usuario || '');

            const response = await fetch(`${API_URL}/estudiante/cursos-extension/inscribir`, {
                method: 'POST',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                body: formData
            });

            if (response.ok) {
                setMensaje({ tipo: 'success', texto: '¡Inscripción enviada! Será revisada por el equipo académico.' });
                loadCursos();
                setTimeout(() => setShowModal(false), 2000);
            } else {
                const error = await response.json();
                setMensaje({ tipo: 'error', texto: error.message || 'Error al inscribirse' });
            }
        } catch (error) {
            setMensaje({ tipo: 'error', texto: 'Error de conexión' });
        } finally {
            setEnviando(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-BO', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader className="animate-spin mr-2" /> Cargando cursos...
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Cursos de Extensión</h1>
                <p className="text-gray-500">Explora y inscríbete en nuestros cursos disponibles</p>
            </div>

            {/* Buscador */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Buscar cursos..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Lista de cursos */}
            {cursos.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <Award size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay cursos disponibles en este momento</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cursos.map(curso => (
                        <div key={curso.id_curso} className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
                            <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                                <Award size={48} className="text-white" />
                            </div>
                            <div className="p-4">
                                <h3 className="font-bold text-lg text-gray-800 mb-2">{curso.nombre}</h3>
                                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{curso.descripcion || 'Sin descripción'}</p>

                                <div className="space-y-2 text-sm text-gray-500 mb-4">
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} />
                                        <span>{formatDate(curso.fecha_inicio)} - {formatDate(curso.fecha_fin)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock size={16} />
                                        <span>{curso.duracion_horas || '?'} horas</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <DollarSign size={16} />
                                        <span className="font-semibold text-green-600">{formatCurrency(curso.costo)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Award size={16} className={curso.disponibles > 0 ? "text-blue-500" : "text-red-500"} />
                                        <span>
                                            <strong>Cupos:</strong> {
                                                curso.disponibles !== undefined && curso.disponibles !== null
                                                    ? (curso.disponibles > 0 ? `${curso.disponibles} disponibles` : 'Sin cupos')
                                                    : (curso.capacidad || '30')
                                            }
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleInscribirse(curso)}
                                    disabled={curso.inscrito || (curso.disponibles <= 0)}
                                    className={`w-full py-2 rounded-lg font-medium transition-colors ${curso.inscrito || (curso.disponibles <= 0)
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-blue-900 text-white hover:bg-blue-800'
                                        }`}
                                >
                                    {curso.inscrito ? 'Ya inscrito' : (curso.disponibles <= 0 ? 'Sin cupos' : 'Inscribirse')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de inscripción */}
            {showModal && selectedCurso && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="bg-blue-600 px-6 py-4 text-white flex justify-between items-center">
                            <h3 className="text-lg font-bold">Inscripción a Curso</h3>
                            <button onClick={() => setShowModal(false)} className="text-white hover:bg-blue-700 p-2 rounded">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="mb-4">
                                <p className="font-semibold text-gray-800">{selectedCurso.nombre}</p>
                                <p className="text-green-600 font-bold text-lg">{formatCurrency(selectedCurso.costo)}</p>
                            </div>

                            {paymentConfig && (
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm">
                                    <p className="font-semibold mb-2">Datos para el pago:</p>
                                    <p>Banco: {paymentConfig.banco}</p>
                                    <p>Cuenta: {paymentConfig.cuenta}</p>
                                    <p>Titular: {paymentConfig.titular}</p>
                                    {paymentConfig.qr_url && (
                                        <img src={paymentConfig.qr_url} alt="QR Pago" className="mt-2 w-32 h-32 mx-auto" />
                                    )}
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Comprobante de pago *
                                </label>
                                <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={(e) => setComprobante(e.target.files[0])}
                                        className="hidden"
                                        id="comprobante-input"
                                    />
                                    <label htmlFor="comprobante-input" className="cursor-pointer">
                                        <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-500">
                                            {comprobante ? comprobante.name : 'Clic para subir comprobante'}
                                        </p>
                                    </label>
                                </div>
                            </div>

                            {mensaje.texto && (
                                <div className={`mb-4 p-3 rounded-lg ${mensaje.tipo === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                    }`}>
                                    {mensaje.texto}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={enviando}
                                className="w-full py-3 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {enviando ? 'Enviando...' : 'Confirmar Inscripción'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CursosExtensionPreadmision;
